// A live output (강의 화면): the content itself, drawn by the learner renderer inside the
// output panel, scrolled like a lecture. Its Studio pages are listed to jump between, and
// the page in view can be edited.

function node(tag, attributes = {}, children = []) {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) {
    if (name === 'text') element.textContent = value;
    else element.setAttribute(name, value);
  }
  element.append(...children);
  return element;
}

/** onEdit(sectionId): open the Studio page in view. */
export function mountLiveOutput({ onEdit }) {
  const pages = node('div', { class: 'es-output-live-pages', role: 'group', 'aria-label': '쪽으로 가기' });
  const position = node('span', { class: 'es-output-reader-position', 'aria-live': 'polite', 'data-testid': 'studio-output-live-position' });
  const edit = node('button', { type: 'button', class: 'es-output-action', 'data-testid': 'studio-output-live-edit', text: '이 쪽 편집하기' });
  const view = node('div', { class: 'es-output-live-view', tabindex: '0', 'aria-label': '강의 화면', 'data-testid': 'studio-output-live-view' });
  const element = node('section', { class: 'es-output-live', hidden: '', 'data-testid': 'studio-output-live' }, [
    node('div', { class: 'es-output-reader-bar' }, [position, edit]), pages, view,
  ]);
  let session = null, sections = [], index = 0, frame = 0, opening = 0;

  function update() {
    frame = 0;
    const line = view.scrollTop + view.clientHeight / 3;
    let found = 0;
    for (let at = 0; at < sections.length && sections[at].offsetTop <= line; at += 1) found = at;
    index = found;
    const section = sections[index];
    if (!section) return;
    position.textContent = `${index + 1} / ${sections.length}쪽 · ${section.querySelector('h2')?.textContent ?? ''}`;
    for (const [at, button] of [...pages.children].entries()) button.setAttribute('aria-current', String(at === index));
  }
  view.addEventListener('scroll', () => { frame ||= requestAnimationFrame(update); });
  edit.addEventListener('click', () => { const section = sections[index]; if (section) onEdit(section.dataset.sectionId); });

  function go(at) {
    const section = sections[at];
    if (!section) return;
    view.scrollTop = section.offsetTop - 12;
    update();
  }

  /** Draws `content` as the learner sees it, from the Studio page `startSectionId` if given. */
  /** `canEdit` false: the content is not edited on its Studio pages here (it is on its output's), so no 이 쪽 편집하기. */
  async function open(content, { startSectionId = null, canEdit = true } = {}) {
    close();
    edit.hidden = !canEdit;
    const ticket = ++opening;
    element.hidden = false;
    const root = node('div', { class: 'es-output-live-document' });
    view.replaceChildren(root);
    const { mountContentReader } = await import('./reader.mjs');
    if (ticket !== opening) return;
    session = mountContentReader(root, content);
    await session.ready;
    if (ticket !== opening) return;
    sections = [...root.querySelectorAll('article > section[data-section-id]')];
    pages.replaceChildren(...sections.map((section, at) => {
      const button = node('button', { type: 'button', 'data-testid': `studio-output-live-page-${at + 1}`, text: `${at + 1}. ${section.querySelector('h2')?.textContent ?? ''}` });
      button.addEventListener('click', () => go(at));
      return button;
    }));
    const start = sections.findIndex(section => section.dataset.sectionId === startSectionId);
    go(Math.max(0, start));
  }
  function close() {
    opening += 1;
    session?.dispose(); session = null;
    sections = []; view.replaceChildren(); pages.replaceChildren();
    element.hidden = true;
  }
  return { element, open, close, get sectionId() { return sections[index]?.dataset.sectionId ?? null; } };
}
